import moment from 'moment';
import React, {ReactNode, useState} from 'react';
import {useForm} from 'react-hook-form';
import {
  Dimensions,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
} from 'react-native';
import {BarChart} from 'react-native-chart-kit';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {
  useGetPOReportsGroupWiseQuery,
  useGetPOSummQuery,
} from '../redux/services/poreportsApi';
import {RootState} from '../redux/store';
import {
  colors,
  spacing,
  shadows,
  borderRadius,
  typography,
} from '../theme/theme';

import T from '../Components/T';
interface ColProps {
  children: ReactNode;
  style?: object;
}

interface RowProps {
  children: ReactNode;
  style?: object;
}

const Col: React.FC<ColProps> = ({children, style}) => {
  return <View style={[{flex: 1}, style]}>{children}</View>;
};

const Row: React.FC<RowProps> = ({children, style}) => {
  return <View style={[{flexDirection: 'row'}, style]}>{children}</View>;
};

const FinancialOverviewScreen = () => {
  const [qrdate, setQrdate] = useState(new Date());
  const gdata = useSelector((state: RootState) => state.gdata);
  let cash = 0,
    bank = 0,
    credit = 0,
    act = 0,
    tax = 0,
    total = 0,
    grtotal = 0;

  const initialvalues = {_date: qrdate};

  const {...methods} = useForm<any>({
    defaultValues: {initialvalues},
  });

  const {data} = useGetPOSummQuery(
    {
      cid: gdata.company,
      fdt: moment(qrdate).format('YYYY-MM-DD'),
      tdt: moment(qrdate).format('YYYY-MM-DD'),
    },
    {refetchOnFocus: true},
  );
  const {data: groupwiseData} = useGetPOReportsGroupWiseQuery(
    {
      cid: gdata.company,
      fdt: moment(qrdate).format('YYYY-MM-DD'),
      tdt: moment(qrdate).format('YYYY-MM-DD'),
    },
    {refetchOnFocus: true},
  );

  const screenWidth = Dimensions.get('window').width;

  let aggregatedData = data
    ? data.reduce(
        (
          acc: {[key: string]: {bl_act: number; bl_cash: number}},
          cur: {bl_areaname: any; bl_act: string; bl_cash: string},
        ) => {
          const area = cur.bl_areaname;
          if (!acc[area]) {
            acc[area] = {bl_act: 0, bl_cash: 0};
          }
          acc[area].bl_act += parseFloat(cur.bl_act);
          acc[area].bl_cash += parseFloat(cur.bl_cash);
          return acc;
        },
        {},
      )
    : {};

  const chartData = {
    labels: Object.keys(aggregatedData),
    datasets: [
      {
        data: Object.values(aggregatedData).map((item: any) => item.bl_act),
        color: (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
      },
      {
        data: Object.values(aggregatedData).map((item: any) => item.bl_cash),
        color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
      },
    ],
    legend: ['Actual Amount', 'Cash Payments'],
  };

  const chartConfig = {
    backgroundColor: colors.white,
    backgroundGradientFrom: colors.white,
    backgroundGradientTo: colors.white,
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(75, 85, 99, ${opacity})`,
    style: {
      borderRadius: borderRadius.lg,
    },
    barPercentage: 0.6,
    propsForBackgroundLines: {
      strokeDasharray: '',
      stroke: colors.borderLight,
      strokeWidth: 1,
    },
    propsForLabels: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
    },
  };

  const isToday = moment(qrdate).isSame(moment(), 'day');

  return (
    <ScrollView
      style={screenStyles.container}
      contentContainerStyle={screenStyles.scrollContent}
      keyboardShouldPersistTaps="handled">
      {/* Date Navigation Pill Bar */}
      <View style={screenStyles.dateBar}>
        <TouchableOpacity
          style={screenStyles.dateNavBtn}
          onPress={() =>
            setQrdate(moment(qrdate).subtract(1, 'days').toDate())
          }
          activeOpacity={0.7}>
          <Icon name="chevron-left" size={14} color={colors.accent} />
        </TouchableOpacity>

        <View style={screenStyles.dateLabelContainer}>
          <Icon name="calendar" size={14} color={colors.accent} style={{marginRight: 8}} />
          <Text style={screenStyles.dateText}>
            {moment(qrdate).format('DD MMMM YYYY')}
          </Text>
          {isToday && (
            <View style={screenStyles.todayBadge}>
              <Text style={screenStyles.todayBadgeText}><T>Today</T></Text>
            </View>
          )}
        </View>

        <TouchableOpacity
          style={[
            screenStyles.dateNavBtn,
            moment(qrdate).add(1, 'day').toDate() > moment().toDate() && screenStyles.disabledBtn,
          ]}
          disabled={moment(qrdate).add(1, 'day').toDate() > moment().toDate()}
          onPress={() => setQrdate(moment(qrdate).add(1, 'days').toDate())}
          activeOpacity={0.7}>
          <Icon
            name="chevron-right"
            size={14}
            color={
              moment(qrdate).add(1, 'day').toDate() > moment().toDate()
                ? colors.mediumGray
                : colors.accent
            }
          />
        </TouchableOpacity>
      </View>

      {/* Counter-wise Sales Chart Card */}
      <View style={screenStyles.chartCard}>
        <View style={screenStyles.cardHeaderRow}>
          <View style={[screenStyles.headerIconBadge, {backgroundColor: colors.lightGradient[0]}]}>
            <Icon name="bar-chart" size={16} color={colors.accent} />
          </View>
          <Text style={screenStyles.sectionTitle}><T>Counter-wise Sales</T></Text>
        </View>
        <BarChart
          data={chartData}
          width={Math.min(screenWidth - 48, 380)}
          height={240}
          yAxisLabel="₹"
          yAxisSuffix=""
          chartConfig={chartConfig}
          verticalLabelRotation={30}
          fromZero
          showBarTops
          showValuesOnTopOfBars
        />
      </View>

      {/* Settlement Breakdown Card */}
      <View style={screenStyles.modernCard}>
        <View style={screenStyles.cardHeaderRow}>
          <View style={[screenStyles.headerIconBadge, {backgroundColor: colors.successLight}]}>
            <Icon name="money" size={16} color={colors.success} />
          </View>
          <Text style={screenStyles.sectionTitle}><T>Settlement Breakdown</T></Text>
        </View>

        <View style={screenStyles.gridHeader}>
          <Text style={[screenStyles.gridHeadCell, {flex: 2}]}><T>Counter</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Cash</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Bank</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Credit</T></Text>
        </View>

        {data
          ? data.map((v: any, i: number) => {
              const areaBank =
                Number(v.bl_card) +
                Number(v.bl_chq) +
                Number(v.bl_bank) +
                Number(v.bl_upi);
              cash += Number(v.bl_cash);
              bank += areaBank;
              credit += Number(v.bl_credit);

              return (
                <View
                  key={i}
                  style={[
                    screenStyles.gridRow,
                    i % 2 === 1 && screenStyles.gridRowAlt,
                  ]}>
                  <Text style={[screenStyles.gridText, {flex: 2}]}>{v.bl_areaname}</Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight, {color: colors.success}]}>
                    ₹{Number(v.bl_cash).toLocaleString()}
                  </Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight, {color: colors.info}]}>
                    ₹{areaBank.toLocaleString()}
                  </Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight, {color: colors.warning}]}>
                    ₹{Number(v.bl_credit).toLocaleString()}
                  </Text>
                </View>
              );
            })
          : null}

        {/* Totals */}
        <View style={screenStyles.gridFooter}>
          <Text style={[screenStyles.footerText, screenStyles.bold, {flex: 2}]}>
            <T>Total</T>
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight, {color: colors.success}]}>
            ₹{cash.toFixed(2)}
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight, {color: colors.info}]}>
            ₹{bank.toFixed(2)}
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight, {color: colors.warning}]}>
            ₹{credit.toFixed(2)}
          </Text>
        </View>
      </View>

      {/* Sales Breakdown Card */}
      <View style={screenStyles.modernCard}>
        <View style={screenStyles.cardHeaderRow}>
          <View style={[screenStyles.headerIconBadge, {backgroundColor: colors.infoLight}]}>
            <Icon name="line-chart" size={16} color={colors.info} />
          </View>
          <Text style={screenStyles.sectionTitle}><T>Sales Breakdown</T></Text>
        </View>

        <View style={screenStyles.gridHeader}>
          <Text style={[screenStyles.gridHeadCell, {flex: 2}]}><T>Counter</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Actual</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Tax</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Total</T></Text>
        </View>

        {data
          ? data.map((v: any, i: number) => {
              act += Number(v.bl_act);
              tax += Number(v.bl_tax);
              total += Number(v.bl_amt);

              return (
                <View
                  key={i}
                  style={[
                    screenStyles.gridRow,
                    i % 2 === 1 && screenStyles.gridRowAlt,
                  ]}>
                  <Text style={[screenStyles.gridText, {flex: 2}]}>{v.bl_areaname}</Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight]}>
                    ₹{Number(v.bl_act).toLocaleString()}
                  </Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight, {color: colors.textSecondary}]}>
                    ₹{Number(v.bl_tax).toLocaleString()}
                  </Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight, screenStyles.bold]}>
                    ₹{Number(v.bl_amt).toLocaleString()}
                  </Text>
                </View>
              );
            })
          : null}

        {/* Totals */}
        <View style={screenStyles.gridFooter}>
          <Text style={[screenStyles.footerText, screenStyles.bold, {flex: 2}]}>
            <T>Total</T>
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight]}>
            ₹{act.toFixed(2)}
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight]}>
            ₹{tax.toFixed(2)}
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight, {color: colors.accent}]}>
            ₹{total.toFixed(2)}
          </Text>
        </View>
      </View>

      {/* Groupwise Breakdown Card */}
      <View style={screenStyles.modernCard}>
        <View style={screenStyles.cardHeaderRow}>
          <View style={[screenStyles.headerIconBadge, {backgroundColor: colors.warningLight}]}>
            <Icon name="tags" size={16} color={colors.warning} />
          </View>
          <Text style={screenStyles.sectionTitle}><T>Groupwise Breakdown</T></Text>
        </View>

        <View style={screenStyles.gridHeader}>
          <Text style={[screenStyles.gridHeadCell, {flex: 2}]}><T>Group</T></Text>
          <Text style={[screenStyles.gridHeadCell, screenStyles.textRight]}><T>Total</T></Text>
        </View>

        {groupwiseData
          ? groupwiseData.map((v: any, i: number) => {
              grtotal += Number(v.gw_amt);

              return (
                <View
                  key={i}
                  style={[
                    screenStyles.gridRow,
                    i % 2 === 1 && screenStyles.gridRowAlt,
                  ]}>
                  <Text style={[screenStyles.gridText, {flex: 2}]}>{v.gw_group}</Text>
                  <Text style={[screenStyles.gridText, screenStyles.textRight, screenStyles.bold]}>
                    ₹{Number(v.gw_amt).toFixed(2)}
                  </Text>
                </View>
              );
            })
          : null}

        {/* Displaying totals */}
        <View style={screenStyles.gridFooter}>
          <Text style={[screenStyles.footerText, screenStyles.bold, {flex: 2}]}>
            <T>Total</T>
          </Text>
          <Text style={[screenStyles.footerText, screenStyles.bold, screenStyles.textRight, {color: colors.accent}]}>
            ₹{grtotal.toFixed(2)}
          </Text>
        </View>
      </View>

      <View style={screenStyles.brandFooter}>
        <Text style={screenStyles.brandText}><T>Powered by BlueHMS</T></Text>
      </View>
    </ScrollView>
  );
};

const screenStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.backgroundSecondary,
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: 80,
  },
  dateBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.white,
    borderRadius: borderRadius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    marginVertical: spacing.sm,
    ...shadows.md,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  dateNavBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.backgroundSecondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabledBtn: {
    backgroundColor: colors.backgroundTertiary,
    opacity: 0.5,
  },
  dateLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  todayBadge: {
    backgroundColor: colors.successLight,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: borderRadius.full,
    marginLeft: spacing.sm,
  },
  todayBadgeText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    color: colors.success,
  },
  chartCard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius['2xl'],
    padding: spacing.lg,
    marginVertical: spacing.xs,
    ...shadows.lg,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  headerIconBadge: {
    width: 32,
    height: 32,
    borderRadius: borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  modernCard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius['2xl'],
    padding: spacing.lg,
    marginVertical: spacing.sm,
    ...shadows.lg,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  gridHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: borderRadius.md,
    marginBottom: spacing.xs,
  },
  gridHeadCell: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    color: colors.textSecondary,
    textTransform: 'uppercase',
  },
  gridRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  gridRowAlt: {
    backgroundColor: colors.backgroundSecondary,
  },
  gridFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
    backgroundColor: colors.successLight,
    borderRadius: borderRadius.lg,
    marginTop: spacing.sm,
  },
  gridText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.charcoal,
    fontWeight: typography.fontWeight.medium,
  },
  bold: {
    fontWeight: typography.fontWeight.bold,
  },
  textRight: {
    textAlign: 'right',
  },
  footerText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textPrimary,
    fontWeight: typography.fontWeight.medium,
  },
  brandFooter: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    marginTop: spacing.sm,
  },
  brandText: {
    fontSize: typography.fontSize.xs,
    color: colors.mediumGray,
    fontWeight: typography.fontWeight.medium,
  },
});

export default FinancialOverviewScreen;
